import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [beniHatirla, setBeniHatirla] = useState(false);
  const [hata, setHata] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login, loading: authLoading } = useAuth();
  const canvasRef = useRef(null);

  // ✅ Otomatik yönlendirme — Context kullanıyor
  useEffect(() => {
    if (user && !authLoading) {
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate]);

  // ✅ "Beni Hatırla" — Email + Şifre (SAHA KULLANIMI İÇİN)
  useEffect(() => {
    const kayitliEmail = localStorage.getItem('beni_hatirla_email');
    const kayitliSifre = localStorage.getItem('beni_hatirla_sifre');
    
    if (kayitliEmail && kayitliSifre) {
      setEmail(kayitliEmail);
      setPassword(kayitliSifre); // ✅ Şifre de dolduruluyor
      setBeniHatirla(true);
    }
  }, []);

  // ✅ Timeout mesajı göster
  useEffect(() => {
    if (location.state?.reason === 'timeout') {
      setHata('⏰ Oturum süresi doldu. Lütfen tekrar giriş yapın.');
    }
  }, [location.state]);

  // 🌌 Parıldayan toz (canvas) — AYNI KALDI
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = [];
    const particleCount = window.innerWidth < 768 ? 40 : 100;
    let mouseX = -100, mouseY = -100;

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 1.2 + 0.3;
        this.speedX = Math.random() * 1 - 0.5;
        this.speedY = Math.random() * 1 - 0.5;
        this.originalSize = this.size;
      }
      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        if (this.x > canvas.width || this.x < 0) this.speedX *= -1;
        if (this.y > canvas.height || this.y < 0) this.speedY *= -1;

        const dx = mouseX - this.x;
        const dy = mouseY - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance < 80) {
          this.size = this.originalSize + (1 - distance / 80) * 3;
        } else {
          this.size = this.originalSize;
        }
      }
      draw() {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'rgba(12, 20, 39, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.update();
        p.draw();
      });
      requestAnimationFrame(animate);
    };
    animate();

    const handleMouseMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // ✅ Login — Backend API'ye istek atar
  const handleLogin = async (e) => {
    e.preventDefault();
    setHata('');
    setLoading(true);

    try {
      // ✅ Backend'e email ve password gönder
      const result = await login(email, password);

      if (result.success) {
        // ✅ "Beni Hatırla" — Email + Şifre (SAHA KULLANIMI İÇİN)
        if (beniHatirla) {
          localStorage.setItem('beni_hatirla_email', email);
          localStorage.setItem('beni_hatirla_sifre', password); // ✅ Şifre de saklanıyor
        } else {
          localStorage.removeItem('beni_hatirla_email');
          localStorage.removeItem('beni_hatirla_sifre'); // ✅ Şifre de temizleniyor
        }

        navigate('/dashboard');
      }
    } catch (error) {
      setHata(error.message || '❌ Bir hata oluştu. Lütfen tekrar deneyin.');
      console.error('Login hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* 🌌 Canvas: Parıldayan toz */}
      <canvas ref={canvasRef} className="particles-canvas"></canvas>

      {/* ✨ Yıldızlar */}
      <div className="stars"></div>
      <div className="stars2"></div>
      <div className="stars3"></div>

      {/* 🌕 Dolunay */}
      <div className="moon">
        <div className="crater1"></div>
        <div className="crater2"></div>
        <div className="crater3"></div>
        <div className="crater4"></div>
      </div>

      {/* Form */}
      <div className="login-container">
        <div className="login-header">
          <h1>Air Makina</h1>
          <p className="subtitle">Sisteme güvenle giriş yapın</p>
        </div>

        <form onSubmit={handleLogin} className="login-form">
          <div className="input-group">
            <input
              type="email"
              id="email"
              placeholder=" "
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
            />
            <label htmlFor="email">E-posta</label>
            <div className="input-line"></div>
          </div>

          <div className="input-group">
            <input
              type="password"
              id="password"
              placeholder=" "
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
            <label htmlFor="password">Şifre</label>
            <div className="input-line"></div>
          </div>

          <div className="remember-forgot">
            <label className="remember">
              <input
                type="checkbox"
                checked={beniHatirla}
                onChange={(e) => setBeniHatirla(e.target.checked)}
                disabled={loading}
              />
              <span className="checkmark"></span>
              <span className="label-text">Beni hatırla</span>
            </label>
          </div>

          <button type="submit" className="login-button" disabled={loading}>
            {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
          </button>

          {hata && <p className="error-text">{hata}</p>}
        </form>
      </div>
    </div>
  );
};

export default Login;